// Custom cookie storage for cross-subdomain authentication
// This enables session sharing between dashboard.gomecano.com, tools.gomecano.com, and amv.gomecano.com

import { SupportedStorage } from '@supabase/supabase-js'

/**
 * Cookie-based storage adapter for Supabase Auth
 * Uses cookies with domain=.gomecano.com to share sessions across subdomains
 */
export class CookieStorage implements SupportedStorage {
  /**
   * Get item from cookie storage
   */
  getItem(key: string): string | null {
    if (typeof document === 'undefined') return null

    const cookies = document.cookie.split(';')
    for (const cookie of cookies) {
      const [cookieName, cookieValue] = cookie.trim().split('=')
      if (cookieName === key) {
        try {
          return decodeURIComponent(cookieValue)
        } catch {
          return cookieValue
        }
      }
    }
    return null
  }

  /**
   * Set item in cookie storage
   * Uses domain=.gomecano.com to share across all subdomains
   * In localhost, uses default domain (no domain attribute)
   */
  setItem(key: string, value: string): void {
    if (typeof document === 'undefined') return

    // Cookie expires in 1 year
    const expiryDate = new Date()
    expiryDate.setFullYear(expiryDate.getFullYear() + 1)

    // Detect if running on localhost
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    
    // In production: use domain=.gomecano.com to share between subdomains
    // In localhost: omit domain to work locally
    const domainPart = isLocalhost ? '' : 'domain=.gomecano.com; '
    const securePart = isLocalhost ? '' : 'secure; ' // Remove secure flag in localhost (http)

    document.cookie = `${key}=${encodeURIComponent(value)}; ${domainPart}path=/; expires=${expiryDate.toUTCString()}; ${securePart}samesite=lax`
  }

  /**
   * Remove item from cookie storage
   */
  removeItem(key: string): void {
    if (typeof document === 'undefined') return

    // Detect if running on localhost
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    
    // In production: use domain=.gomecano.com
    // In localhost: omit domain
    const domainPart = isLocalhost ? '' : 'domain=.gomecano.com; '
    const securePart = isLocalhost ? '' : 'secure; '

    // Set expiry date in the past to delete cookie
    document.cookie = `${key}=; ${domainPart}path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; ${securePart}samesite=lax`
  }
}


